/**
 * Users, roles and who holds what.
 *
 * The permission list is fetched rather than written here: it is the product's own surface,
 * and a client that keeps its own copy shows a checkbox for something the server has never
 * heard of the first time the two versions differ.
 */
import { request } from './client';

export type RoleScope = 'Global' | 'Operation' | 'Region' | 'Location';

export interface Role {
  id: string;
  key: string;
  name: string;
  description: string | null;
  /** Built in: cannot be deleted, and its permissions cannot be edited. */
  isSystem: boolean;
  isActive: boolean;
  permissions: string[];
  holderCount: number;
}

/** Where a role came from. Only a direct grant is a row on the person. */
export type RoleSource = 'Position' | 'Profession' | 'Group' | 'Direct';

export interface Assignment {
  /** The grant's own row — only a direct one has it. Empty for the rest. */
  id: string;
  roleId: string;
  roleKey: string;
  roleName: string;
  scope: RoleScope;
  scopeId: string | null;
  scopeName: string | null;

  /**
   * Seat, trade, group, or granted to the person.
   *
   * <p>Decides what can be done about it: the chart's are undone by moving somebody, not
   * from here, so only a direct grant is offered a way out.</p>
   */
  source: RoleSource;

  /** The seat, trade or group it arrived through. Null for a direct grant. */
  through: string | null;
  reachesNowhere: boolean;
}

export interface User {
  id: string;
  email: string;
  fullName: string;
  isActive: boolean;

  /** Head office, named individually rather than through a department. */
  isHeadOffice: boolean;
  /** False for an account created ahead of an invitation. It cannot sign in yet. */
  hasPassword: boolean;
  lockedUntil: string | null;
  assignments: Assignment[];
  permissions: string[];
}

export interface ScopeTarget {
  id: string;
  name: string;
  scope: RoleScope;
}

export interface SaveRole {
  key: string;
  name: string;
  description: string | null;
  isActive: boolean;
  permissions: string[];
}

export interface SaveUser {
  email: string;
  fullName: string;
  isActive: boolean;
  isHeadOffice: boolean;
  initialPassword?: string | null;
}

export interface GrantRole {
  roleId: string;
  scope: RoleScope;
  scopeId: string | null;
}

const base = '/api/v1/access';
const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) });

export const access = {
  permissions: () => request<{ key: string }[]>(`${base}/permissions`),
  scopeTargets: () => request<ScopeTarget[]>(`${base}/scope-targets`),

  roles: {
    list: () => request<Role[]>(`${base}/roles`),
    create: (body: SaveRole) => request<Role>(`${base}/roles`, json('POST', body)),
    update: (id: string, body: SaveRole) => request<Role>(`${base}/roles/${id}`, json('PUT', body)),
    remove: (id: string) => request<void>(`${base}/roles/${id}`, { method: 'DELETE' }),
  },

  users: {
    list: () => request<User[]>(`${base}/users`),
    create: (body: SaveUser) => request<User>(`${base}/users`, json('POST', body)),
    update: (id: string, body: SaveUser) => request<User>(`${base}/users/${id}`, json('PUT', body)),
    setPassword: (id: string, password: string) =>
      request<User>(`${base}/users/${id}/password`, json('POST', { password })),
    grant: (id: string, body: GrantRole) =>
      request<User>(`${base}/users/${id}/roles`, json('POST', body)),
    remove: (id: string) => request<void>(`${base}/users/${id}`, { method: 'DELETE' }),
    revoke: (id: string, assignmentId: string) =>
      request<User>(`${base}/users/${id}/roles/${assignmentId}`, { method: 'DELETE' }),
  },
};
